import {Currency} from "@nolimitcity/slot-game/bin/core/balance/Currency";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineLite, TweenLite} from "gsap";
import {FITHGameConfig} from "../../FITHGameConfig";
import {IMultiSpritesConfig, MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";

export class FITHInitialWPView extends BaseView {
    private readonly _winNumberTotalDuration: number = 2;
    private readonly _winNumberFadeOutDuration: number = 1.3;
    private readonly _winNumberOffsetY: number = -100;
    private readonly _winNumberInterval: number = 0.2;
    private readonly _maxWinNumberWidth: number = FITHGameConfig.instance.SYMBOL_WIDTH;
    private readonly _wayWonTexts: string[] = ["wayWon", "waysWon"];
    private readonly _maxWidth: number = FITHGameConfig.instance.REEL_AREA_WIDTH * 0.9;
    private _layer: ScalableLayer;

    constructor() {
        super();
        this._layer = StageManager.getLayer(FITHGameConfig.instance.LAYERS.WIN_NUMBERS.name);
    }

    public playWinNumbers(betWayWinData: IBetWayWinData, startPosition: [number, number]): TimelineLite {
        let text: string = Currency.formatValue(MathHelper.roundToDecimals(betWayWinData.amount, 2));
        if (betWayWinData.multiplier > 1) {
            text += ` x ${betWayWinData.multiplier}`;
        }

        const timeline: TimelineLite = new TimelineLite({paused: true});
        timeline.add(() => {
            this.playWinNumberAnimation(text, startPosition);
            this.playWaysAnimation(betWayWinData, startPosition);
        }, this._winNumberInterval);

        return timeline;
    }

    // do not abort this ever, like wixx.
    private playWinNumberAnimation(text: string, startPosition: [number, number]): void {
        const winNumber: PIXI.BitmapText = new PIXI.BitmapText(text, {
            fontName: "NumbersWinCountUp",
            fontSize: 12,
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        (<PIXI.Point>winNumber.anchor).set(0.5);
        winNumber.position.set(startPosition[0], startPosition[1] - 20);
        this._layer.addChild(winNumber);
        winNumber.updateTransform();
        if (winNumber.width > this._maxWinNumberWidth) {
            const scale: number = MathHelper.roundToDecimals(this._maxWinNumberWidth / winNumber.width, 2);
            winNumber.scale.set(scale);
        }

        const timeline: TimelineLite = new TimelineLite();
        timeline.add(TweenLite.to(winNumber, this._winNumberTotalDuration, {y: startPosition[1] + this._winNumberOffsetY}), this._winNumberInterval);
        timeline.add(TweenLite.to(winNumber, this._winNumberFadeOutDuration, {alpha: 0}), `-=${this._winNumberFadeOutDuration}`);
        timeline.add(() => {
            this._layer.removeChild(winNumber);
            winNumber.destroy();
        });
    }

    // do not abort this ever, like wixx.
    private playWaysAnimation(betWayWinData: IBetWayWinData, startPosition: [number, number]): void {
        const waysWonContainer: PIXI.Container = new PIXI.Container(),
            waysWon: number = betWayWinData.numberOfCombinations,
            wayWonText: string = ((waysWon === 1) ? this._wayWonTexts[0] : this._wayWonTexts[1]);

        const waysWonConfig: IMultiSpritesConfig = {};
        this._wayWonTexts.forEach((text: string) => {
            waysWonConfig[text] = {
                textures: GameResources.getTextures(`${text}Text`),
                anchor: [0, 0]
            }
        });
        const waysWonText: MultiSprites = new MultiSprites(waysWonConfig);
        waysWonText.scale.set(0.4);
        waysWonContainer.addChild(waysWonText);

        const waysNumber: PIXI.BitmapText = new PIXI.BitmapText("" + waysWon, {
            fontName: "NumbersWaysWon",
            fontSize: 16,
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        waysWonContainer.addChild(waysNumber);

        waysWonContainer.position.set(startPosition[0] + 45, startPosition[1] + 45);
        waysWonContainer.pivot.set(waysWonContainer.width / 2, waysWonContainer.height / 2);
        this._layer.addChild(waysWonContainer);
        this.updatePositions(wayWonText, waysNumber, waysWonText, waysWonContainer);
        waysWonContainer.alpha = 1;

        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            TweenLite.to(waysWonContainer, this._winNumberTotalDuration, {y: startPosition[1] + this._winNumberOffsetY + 50}),
            waysWonText.playSprite(wayWonText,
                {
                    duration: 0,
                    vars: [{fromVars: {alpha: 0}, toVars: {alpha: 1}}]
                }
            )
        ], 0);
        timeline.add(TweenLite.to(waysWonContainer, this._winNumberFadeOutDuration, {alpha: 0}), `-=${this._winNumberFadeOutDuration}`);
        timeline.add(() => {
            this._layer.removeChild(waysWonContainer);
            waysWonContainer.children.forEach((item: PIXI.DisplayObject) => item.destroy());
            waysWonContainer.removeChildren();
            waysWonContainer.destroy();
        });
    }

    private updatePositions(spriteName: string, waysWonNumber: PIXI.BitmapText, waysWonText: MultiSprites, waysWonContainer: PIXI.Container): void {
        waysWonNumber.updateTransform();
        const totalWidth: number = waysWonNumber.width + waysWonText.sprites[spriteName].width;
        waysWonNumber.x = -totalWidth / 2;
        waysWonText.x = waysWonNumber.x + waysWonNumber.width;

        if (totalWidth * waysWonContainer.scale.x > this._maxWidth) {
            const scale: number = MathHelper.roundToDecimals(this._maxWidth / totalWidth, 2);
            waysWonContainer.scale.set(scale);
        }
    }
}